<template>
  <div class="h-screen overflow-hidden flex flex-col justify-between bg-white border-solid border-l border-0 border-gray-border">
    <div>
      <div class="border-solid border-b border-0 border-gray-border px-8 pb-8">
        <input-with-error-message v-if="isRenameMode"
                                  id="index-editor-name-input"
                                  :value.sync="indexName"
                                  :is-loading="isLoadingName"
                                  @escape="$emit('set-rename-mode', false)"
                                  @submit="$emit('rename',{ ...index, name: $event })"
                                  class="h-full mt-5 w-2/3"
        />
        <div v-else class="mt-8 inline-block pr-14 relative">
          <h3 class="font-semibold">
            {{ index.name }}
          </h3>
          <side-list-menu v-if="!isRenameMode"
                          :items="menuItems"
                          class="absolute top-0 right-0 -mt-1"
                          @rename="$emit('set-rename-mode', true)"
                          @reindex-all-sources="$emit('start-reindex', index)"
                          @delete="$emit('request-delete', index)"
          />
        </div>
      </div>

      <settings-index-scheduler :schedule-params="scheduleParams"
                                @update-schedule-params="updateScheduleParams"
                                class="mt-8 px-8"
      />
    </div>

    <div class="grid grid-cols-2 gap-4 mb-10 px-8">
      <busy-button id="saveButton"
                   :variant="saveButtonVariant"
                   class="col-span-1"
                   :is-loading="isSavingIndex"
                   :is-disabled="isSavingIndex"
                   @click="saveIndex"
      >
        {{ saveButtonText }}
      </busy-button>

      <busy-button id="reindexButton"
                   variant="bordered"
                   class="col-span-1"
                   :is-loading="isLoadingReindex"
                   :is-disabled="isLoadingReindex"
                   @click="$emit('start-reindex', index)"
      >
        Reindex now
      </busy-button>
    </div>
  </div>
</template>

<script>
import BusyButton from '@/components/common/BusyButton'
import InputWithErrorMessage from '@/components/common/InputWithErrorMessage.vue'
import SettingsIndexScheduler from '@/components/settings/indexes/SettingsIndexScheduler.vue'
import SideListMenu from '@/components/common/SideListMenu'
import indexesService from '@/services/amio/indexes.service'
import toaster from '@/utils/toaster'
import { MENU_ITEMS, SAVE_BUTTON_VARIANTS } from '@/utils/editor.constants'
import { SAVE_BUTTON_LABELS } from '@/utils/text.constants'
import head from 'ramda/src/head'
import isEmpty from 'ramda/src/isEmpty'

const UPDATE_BUTTON_LABEL = 'Save'
const SUCCESS_BUTTON_LABEL = 'Saved!'

export default {
  name: 'SettingsIndexEditor',
  components: {
    BusyButton,
    InputWithErrorMessage,
    SideListMenu,
    SettingsIndexScheduler
  },
  props: {
    index: {
      type: Object,
      required: true
    },
    isLoadingName: {
      type: Boolean,
      default: false
    },
    isLoadingReindex: {
      type: Boolean,
      default: false
    },
    isRenameMode: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    index: {
      immediate: true,
      handler(index) {
        this.indexName = index.name
        this.scheduleParams = index.schedule_params || null
      }
    }
  },
  data() {
    return {
      indexName: '',
      scheduleParams: null,
      saveButtonVariant: SAVE_BUTTON_VARIANTS.DEFAULT,
      saveButtonText: UPDATE_BUTTON_LABEL,
      error: null,
      isSavingIndex: false,
      isEmpty
    }
  },
  computed: {
    menuItems() {
      return [MENU_ITEMS.rename, MENU_ITEMS.reindexAllSources, MENU_ITEMS.delete]
    }
  },
  methods: {
    updateScheduleParams(scheduleParams) {
      this.scheduleParams = scheduleParams
    },

    async saveIndex() {
      this.error = null
      this.isSavingIndex = true
      try {
        const payload = {
          schedule_params: this.scheduleParams
        }
        await indexesService.update(this.index.id, payload)
        this.$emit('reload-indexes')
      } catch (err) {
        const data = err.response?.data
        const errorMessage = head(data?.details.errors)?.message || err.message
        this.error = errorMessage
        toaster.error(errorMessage)
      } finally {
        this.startButtonStateTimeout()
        this.isSavingIndex = false
      }
    },

    startButtonStateTimeout() {
      this.setButtonProps(this.error ? 'ERROR' : 'SUCCESS')
      this.buttonStateTimeout = setTimeout(() => {
        this.setButtonProps()
      }, 3000)
    },

    setButtonProps(buttonState = 'DEFAULT') {
      this.saveButtonVariant = SAVE_BUTTON_VARIANTS[buttonState]
      if (buttonState === 'DEFAULT') {
        this.saveButtonText = UPDATE_BUTTON_LABEL
      } else if (buttonState === 'SUCCESS') {
        this.saveButtonText = SUCCESS_BUTTON_LABEL
      } else {
        this.saveButtonText = SAVE_BUTTON_LABELS[buttonState]
      }
    }
  }
}
</script>
